import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { fetchCatalogue, checkoutAndRedirect, formatCents } from '../../services/stripeBilling';
import styles from './Boosters.module.css';

// Boosters were three static marketing chips with no way to buy one. They are buttons now.
//
// Prices come from /api/billing/catalogue rather than being hardcoded here, which is how
// the old $29 chip and the real $29 Stripe price stay in agreement. The static amounts
// below are a render fallback only, for when the catalogue cannot be read: a pricing
// section showing nothing is worse than one that is briefly stale.
const BOOSTERS = [
  { itemKey: 'leads_50',    amount: '+50 leads',       fallback: 1900, note: 'Verified, still-in-role' },
  { itemKey: 'verify_500',  amount: '+500 verifies',   fallback: 900,  note: 'Email verifications' },
  { itemKey: 'verify_2000', amount: '+2,000 verifies', fallback: 2900, note: 'Email verifications' },
];

// `compact` drops the marketing heading and the outer section padding so the same component
// can sit inside an Account card. Boosters used to be reachable ONLY from the pricing page,
// which meant the one moment a user wants to top up (looking at a nearly-empty meter) was
// the one place they could not.
export default function Boosters({ compact = false }) {
  const { user, getToken } = useAuth();
  const navigate = useNavigate();
  const [catalogue, setCatalogue] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const c = await fetchCatalogue({ token: getToken?.() });
      if (alive) setCatalogue(c);
    })();
    return () => { alive = false; };
    // getToken is deliberately not a dependency: it is not referentially stable, and the
    // token is re-read on every run anyway. Mirrors useAccessStatus.
  }, [user?.id]);

  async function buy(itemKey) {
    // A visitor with no account cannot own a subscription to top up, so send them to sign
    // up rather than into a checkout that the backend would refuse.
    if (!user) { navigate('/register'); return; }

    setBusy(itemKey);
    setError(null);
    try {
      await checkoutAndRedirect(itemKey, { token: getToken?.() });
    } catch (err) {
      // The product copy says a booster "requires an active RepBuddy subscription", and the
      // backend enforces it. Selling credits to someone with no plan would take money for
      // something they cannot spend, since entitlement gates the whole app.
      setError(err.code === 'no_subscription'
        ? 'Boosters top up an active plan. Choose a plan first, then add a booster any time.'
        : (err.message || 'Could not start checkout. Please try again.'));
      setBusy(null);
    }
  }

  const priceOf = (b) => catalogue?.items?.[b.itemKey]?.amountCents ?? b.fallback;

  const chips = (
    <>
        <div className={styles.chips}>
          {BOOSTERS.map((b) => (
            <button
              key={b.itemKey}
              type="button"
              className={styles.chip}
              onClick={() => buy(b.itemKey)}
              disabled={busy !== null}
              title={b.note}
            >
              <span className={styles.amount}>{b.amount}</span>
              <span className={styles.price}>
                {busy === b.itemKey ? '…' : formatCents(priceOf(b))}
              </span>
            </button>
          ))}
        </div>
      {error && <p className={styles.note} role="alert">{error}</p>}
    </>
  );

  if (compact) return <div className={styles.compact}>{chips}</div>;

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <div className={styles.head}>
          <h2 className={styles.heading}>Need more mid-month? Grab a booster.</h2>
          <p className={styles.note}>Boosters never expire.</p>
        </div>
        {chips}
      </div>
    </section>
  );
}
